/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Text, VStack, useToast, Grid } from '@chakra-ui/react';
import { useTheme } from '../../contexts/ThemeProvider';
import { BannerCarousel } from '../../components/molecules/Carousel';
import { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { MainTemplate } from '../../templates/MainTemplate';
import { useProfile } from '@/contexts/ProfileProvider';
import { APP_EVENTS, spawnAppEvent, ebooksDomain } from '@/service/utils';
import { benefits_economizar } from './utils';
import { Label, BenefitsItem } from './components';

const isBookstoreProduct = (item: any) => !!item?.default_product?.is_bookstore;
const isMagazineProduct = (item: any) => item?.metadata?.hasMagazine?.value === 'true';
const isTelemedicineProduct = (item: any) =>
  item?.default_product?.id === 5 || item?.default_product?.name === 'Telemedicina';

const TELEMEDICINE_PATH = '/telemedicina';

const resolveModuleUrl = (item: any): string => {
  // Telemedicina é rota interna e o produto não traz url no metadata
  if (isTelemedicineProduct(item)) return TELEMEDICINE_PATH;
  if (item?.url && typeof item.url === 'string' && item.url.startsWith('http')) {
    return item.url;
  }
  const metaUrl = item?.metadata?.url?.value;
  if (metaUrl && typeof metaUrl === 'string' && metaUrl.startsWith('http')) {
    return metaUrl;
  }
  if (item?.metadata?.slug?.value) {
    return `https://${item.metadata.slug.value}.${ebooksDomain}/`;
  }
  return item?.url ?? '';
};

export function ProdutosPage() {
  const {
    banners,
    logo,
    isApp,
    benefits,
    productBenefits,
    isNative,
    colors,
    openModalLogin,
    isProductSuperApp,
    integrationId,
    customerCentralModule,
    disableLibraryModule,
  } = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState(false);

  const { profile } = useProfile();
  const navigate = useNavigate();
  const toast = useToast();

  const benefitsFormated = useMemo<any[]>(() => {
    if (!isProductSuperApp) {
      if (!benefits) return [];
      const titleOrder = ['Economizar', 'Benefícios Para Ler', 'Benefícios'];
      const rank = (title: string) => {
        const idx = titleOrder.indexOf(title);
        return idx === -1 ? titleOrder.length : idx;
      };
      return [...benefits].sort((a, b) => rank(a.title) - rank(b.title));
    }

    if (!productBenefits) return [];

    const isSuperapp = (p: any) => !!p.default_product?.is_superapp;
    const mainProduct = productBenefits.find(isSuperapp);

    const hasEcommerce = mainProduct?.metadata?.ecommerceModule?.value === 'true';
    const hasLocal = mainProduct?.metadata?.localModule?.value === 'true';
    const economizarModules = benefits_economizar.filter((benefit: any) => {
      if (hasEcommerce && benefit?.title === 'E-commerce') return true;
      if (hasLocal && benefit?.title === 'Lojas Físicas') return true;
      if (benefit?.title === 'Cinema' && (hasEcommerce || hasLocal)) return true;
      return false;
    });

    const bookstoreOrder = ['Literários', 'Educa', 'Infantil', 'Profissional'];
    const bookstoreRank = (name: string) => {
      const idx = bookstoreOrder.indexOf(name);
      return idx === -1 ? bookstoreOrder.length : idx;
    };

    const bookstoreModules = !disableLibraryModule
      ? []
      : productBenefits
          .filter(isBookstoreProduct)
          .sort((a: any, b: any) => bookstoreRank(a?.name) - bookstoreRank(b?.name));

    const svaGroups = new Map<string, any[]>();
    for (const product of productBenefits) {
      if (isSuperapp(product) || isBookstoreProduct(product)) continue;
      const category = product.metadata?.category?.value || 'Outros';
      const bucket = svaGroups.get(category);
      if (bucket) bucket.push(product);
      else svaGroups.set(category, [product]);
    }

    return [
      { title: 'Economizar', modules: economizarModules },
      { title: 'Benefícios Para Ler', modules: bookstoreModules },
      ...Array.from(svaGroups, ([title, modules]) => ({ title, modules })),
    ].filter((group) => group.modules.length > 0);
  }, [benefits, isProductSuperApp, productBenefits, disableLibraryModule]);

  const navigateToBookstore = (item: any) => {
    // ?produto= diz qual acervo abrir (Educa, Profissional, ...); sem ele o
    // adapter cai no primeiro produto bookstore do tema.
    const path = isMagazineProduct(item) ? '/banca' : '/biblioteca';
    navigate(item?.id ? `${path}?produto=${item.id}` : path, {
      state: { from: '/produtos' },
    });
  };

  const onNavigateModule = async (url: string, isExternal: boolean, metadata?: any) => {
    if (!profile) {
      openModalLogin();
      return;
    }

    if (!url) {
      toast({
        title: 'Erro',
        description: 'URL não disponível para este benefício',
        status: 'error',
      });
      return;
    }

    if (integrationId === 'ixc' && customerCentralModule && metadata?.autoLoginOnRedirect?.value === 'true') {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'cpf_cnpj';
      input.value = (profile.cpf || '').replace(/\D/g, '');
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      return;
    }

    setIsLoading(true);
    try {
      if (url === TELEMEDICINE_PATH) {
        navigate(url);
      } else if (isExternal) {
        if (isApp) {
          spawnAppEvent(APP_EVENTS.OPEN_LINK, url);
        } else {
          window.open(url, '_blank');
        }
      } else if (url.includes('http')) {
        navigate(url);
      } else if (!isNative) {
        navigate('/vantagens', { state: { action: url } });
      } else {
        toast({
          title: 'Oops',
          description: 'Essa ação irá filtrar as vantagens no seu App',
          status: 'error',
        });
      }
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao processar dados do benefício',
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!benefitsFormated.length || action) return;

    const params = Object.fromEntries(new URLSearchParams(location.search));
    const actionParam = params.action;
    if (!actionParam) return;

    if (!actionParam.startsWith('http')) {
      const module = productBenefits?.find((benefit: any) => benefit.id === Number(actionParam));
      if (!module) {
        toast({ title: 'Erro', description: 'Benefício não encontrado', status: 'error' });
        return;
      }
      if (isBookstoreProduct(module)) {
        navigateToBookstore(module);
      } else {
        onNavigateModule(
          resolveModuleUrl(module),
          module.metadata?.isExternalLink?.value === 'true',
          module.metadata
        );
      }
    } else {
      const findBenefit = benefits?.find((benefit: any) =>
        benefit.modules?.find((m: any) => m.url === actionParam)
      );
      const module = findBenefit?.modules?.find((m: any) => m.url === actionParam);
      if (!module) return;
      onNavigateModule(module.url, module.is_external, module.metadata);
    }
    setAction(true);
  }, [benefitsFormated, action]);

  return (
    <MainTemplate hideBottomMenu={isNative} hideTopMenu={isNative}>
      <VStack
        align={'self-start'}
        width={'100%'}
        marginBottom={isNative ? '0px' : '100px'}
        paddingTop={'10px'}
        spacing={4}
      >
        {isLoading && (
          <Box
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            zIndex={1000}
            bg="white"
            p={4}
            borderRadius="md"
            boxShadow="lg"
          >
            <Text>Carregando benefício...</Text>
          </Box>
        )}

        <BannerCarousel isNative={isNative} banners={banners} />

        {isProductSuperApp && (
          <Box
            width="100%"
            bg={`${colors.primaryColor}1A`}
            borderLeft="4px solid"
            borderColor={colors.primaryColor}
            borderRadius="12px"
            p={{ base: 3, md: 4 }}
            mt={2}
            mb={2}
          >
            <Text color="#4A5568" fontSize="16px" mb={1} fontFamily="Glory">
              Além dos produtos, você tem os serviços abaixo disponíveis como parte do seu combo.
            </Text>
          </Box>
        )}

        {benefitsFormated.length > 0 &&
          benefitsFormated.map((benefit, index) => {
            const isBookstoreSection = benefit.title === 'Benefícios Para Ler';
            return (
              <VStack
                key={`${benefit.title}-${index}`}
                width={'100%'}
                align={'self-start'}
                spacing={4}
                padding={{ base: isNative ? '10px' : '20px' }}
              >
                <Label>{benefit.title}</Label>

                <Grid
                  width={'100%'}
                  templateColumns={{
                    base: 'repeat(3, 1fr)',
                    sm: 'repeat(3, 1fr)',
                    md: 'repeat(5, 1fr)',
                    lg: 'repeat(6, 1fr)',
                    xl: 'repeat(7, 1fr)',
                  }}
                  gap={isNative ? 2 : 6}
                  alignItems="stretch"
                >
                  {benefit.modules?.map((item: any) => {
                    const moduleUrl = resolveModuleUrl(item);
                    const handleNavigate = isBookstoreSection
                      ? () => {
                          if (!profile) {
                            openModalLogin();
                            return;
                          }
                          navigateToBookstore(item);
                        }
                      : onNavigateModule;

                    return (
                      <Box
                        key={item.id || item.name || `${item.title}-${index}`}
                        display="flex"
                        flexDirection="column"
                        height="100%"
                      >
                        <BenefitsItem
                          title={item.name || item.title}
                          icon={item.icon || item.image || item.metadata?.logo?.value || logo}
                          is_external={item.is_external || item.metadata?.isExternalLink?.value === 'true'}
                          url={moduleUrl}
                          metadata={item.metadata}
                          is_gif={item.is_gif}
                          section_title={item.section_title || item.metadata?.category?.value}
                          isNative={isNative}
                          benefitTitle={benefit.title}
                          maskColor={colors.primaryColor}
                          onNavigateModule={handleNavigate}
                          isLoading={isLoading}
                        />
                      </Box>
                    );
                  })}
                </Grid>
              </VStack>
            );
          })}

        {benefitsFormated.length === 0 && (
          <VStack width={'100%'} margin={'auto'} align={'center'} justifyContent={'center'}>
            <Text color="#747474" fontWeight={'500'} fontSize={'18px'} fontFamily={'Glory'} textAlign={'center'}>
              Não há produtos disponíveis no momento
            </Text>
          </VStack>
        )}
      </VStack>
    </MainTemplate>
  );
}
